import {
    dynamicThemeNameMaxLength,
    type ThemeLocation,
    ThemePreference,
    ThemePreferenceTextMap,
} from "@albertine/types/theme";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";
import { Button as ButtonUILib } from "@/components/ui/button";
import { excludeUndefined } from "../../common/utils/array.util";
import { formatTenantName } from "../../common/utils/tenant.util";
import { EventEmitter } from "../../EventEmitter";
import { scrollRefToTop } from "../../utils/window.util";
import SelectTags from "../CatalogBeta/SelectTags";
import Button from "../Common/Buttons";
import { Flex, FlexInline } from "../Common/Flex";
import { Form } from "../Common/Form/Form";
import Input from "../Common/Input";
import Label from "../Common/Label";
import SelectArticles from "../Common/SelectArticles";
import SelectLocations from "../Common/SelectLocations";
import Tag from "../Common/Tag";
import { TenantTabs } from "../Common/TenantTabs";
import Toggle from "../Common/Toggle";
import { TitleValuePair } from "../MemberData/TitleValuePairs";
import "./ThemeForm.css";
import { useDelete, useGet, usePatch, usePost } from "@albertine/api-client";
import type {
    CatalogItem,
    StaticThemeContentInput,
    Tenant,
    Theme,
    ThemeContentTenant,
} from "@albertine/types/openapi/lmt";
import { TenantSchema } from "@albertine/types/openapi/lmt";
import { XIcon } from "lucide-react";
import {
    type StaticThemeFormContent,
    StaticThemeFormSchema,
} from "../../common/types/DynamicTheme";
import { useClaims } from "../../utils/claims.util";
import { Spinner } from "../ui/spinner";

type ThemeFormProps = {
    theme: Theme | undefined;
    onCancel: () => void;
    onDelete: () => void;
    onSave: (theme: Theme) => void;
};

function ThemeForm(props: ThemeFormProps) {
    const { theme, onCancel, onDelete, onSave } = props;

    const scrollElementRef = useRef<HTMLDivElement>(null);
    const { isAdmin } = useClaims();

    // Check if theme has any dynamic content
    const hasDynamicContent = theme?.content.some((c) => c.type === "dynamic");

    const allArticleIdsSet = new Set<string>();
    if (theme) {
        for (const content of theme.content) {
            if (content.type === "static") {
                for (const id of content.catalogItemIds) {
                    allArticleIdsSet.add(id);
                }
            }
        }
    }
    const allArticleIds = Array.from(allArticleIdsSet.values()).sort();

    const { data: allArticles, isLoading: isFetchingMissingArticles } = useGet(
        "catalogItemsSearch",
        {
            query: {
                ids: allArticleIds,
                // No published filter - show already-selected unpublished items
                categories: [],
                supplierLmtIds: [],
                sources: [],
                tags: [],
                pageSize: String(allArticleIds.length || 1),
            },
        },
        {
            enabled: allArticleIds.length > 0,
        },
    );

    const allArticlesById = new Map<string, CatalogItem>();
    if (allArticles) {
        for (const item of allArticles.catalogItems) {
            allArticlesById.set(item.id, item);
        }
    }

    const { mutate: deleteThemeMutation, isPending: isDeleting } = useDelete(
        "deleteTheme",
        {
            onSuccess: () => {
                onDelete();
            },
            onError: (error) => {
                Sentry.captureException(error, {
                    extra: { message: `Failed to delete theme ${theme?.id}` },
                });
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: `Failed to delete theme "${theme?.name}"`,
                        type: "error",
                    },
                });
            },
        },
    );
    const { mutate: createThemeMutation, isPending: isCreating } = usePost(
        "createTheme",
        {
            onSuccess: (result) => {
                onSave(result);
            },
            onError: (error, request) => {
                Sentry.captureException(error, {
                    extra: { message: `Failed to create new theme` },
                });

                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: `Creating theme "${request.json.name}" failed`,
                        type: "error",
                    },
                });
            },
        },
    );
    const { mutate: updateThemeMutation, isPending: isUpdating } = usePatch(
        "updateTheme",
        {
            onSuccess: (result) => {
                onSave(result);
            },
            onError: (error) => {
                Sentry.captureException(error, {
                    extra: { message: `Failed to update theme ${theme?.id}` },
                });
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: `Updating theme "${theme?.name}" failed`,
                        type: "error",
                    },
                });
            },
        },
    );
    const loading = isDeleting || isCreating || isUpdating;

    const initialContent = themeToFormContent(theme);

    const {
        getValues,
        setValue,
        handleSubmit,
        trigger,
        watch,
        formState: { errors, isSubmitted, isValid, defaultValues },
    } = useForm<StaticThemeFormSchema>({
        resolver: zodResolver(StaticThemeFormSchema),
        defaultValues: {
            name: theme?.name || undefined,
            content: initialContent,
            preferences: theme?.preferences || [],
            published: theme?.publishedAt || null,
            location: theme?.location || [],
        },
    });

    // Watch content to re-render when tabs change
    const formContent = watch("content");

    const themeIsDeletable =
        theme && !hasDynamicContent && theme.placement !== "spotlight";
    const canDeleteTheme = themeIsDeletable && isAdmin;
    const deleteTheme = async () => {
        if (!canDeleteTheme) return;
        if (!window.confirm(`Are you sure you want to delete theme ${theme.name}?`)) {
            return;
        }
        deleteThemeMutation({
            param: { themeId: theme.id },
        });
    };

    const onSubmit: SubmitHandler<StaticThemeFormSchema> = async (formValues) => {
        try {
            // Check for dynamic content in the existing theme
            if (theme?.content.some((c) => c.type === "dynamic")) {
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: "Updating theme with dynamic content is not supported",
                        type: "error",
                    },
                });
                return;
            }

            const published = formValues.published !== null;

            // Convert form content to API content
            const newThemeContent: StaticThemeContentInput[] = formValues.content.map(
                (c) => ({
                    type: "static" as const,
                    tenant: c.tenant,
                    catalogItemIds: c.catalogItemIds,
                }),
            );

            if (theme) {
                updateThemeMutation({
                    param: { themeId: theme.id },
                    json: {
                        name: formValues.name,
                        content: newThemeContent,
                        preferences: formValues.preferences,
                        location: formValues.location,
                        published,
                    },
                });
            } else {
                createThemeMutation({
                    json: {
                        name: formValues.name,
                        content: newThemeContent,
                        preferences: formValues.preferences,
                        location: formValues.location,
                        published,
                    },
                });
            }
        } catch (error) {
            const errorMessage = theme
                ? `Failed to update theme ${theme.id}`
                : `Failed to create new theme`;
            Sentry.captureException(error, {
                extra: { message: errorMessage },
            });

            const apiError = error as { error?: string };
            const message = theme
                ? (apiError.error ?? `Updating theme "${formValues.name}" failed`)
                : (apiError.error ?? `Creating theme "${formValues.name}" failed`);
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message,
                    type: "error",
                },
            });
        }
    };

    const onInvalid: SubmitErrorHandler<StaticThemeFormSchema> = () => {
        trigger();
        scrollRefToTop(scrollElementRef, true);
    };

    const errorTexts = Object.values(errors)
        .map((it) => it.message)
        .filter(excludeUndefined);

    const getArticlesForTenant = (tenant: ThemeContentTenant): CatalogItem[] => {
        const content = formContent.find((c) => c.tenant === tenant);
        if (!content) return [];

        return content.catalogItemIds
            .map((id) => allArticlesById.get(id))
            .filter(excludeUndefined);
    };

    const updateArticlesForTenant = (
        tenant: ThemeContentTenant,
        newArticles: CatalogItem[],
    ) => {
        const currentContent = getValues("content");
        const contentIndex = currentContent.findIndex((c) => c.tenant === tenant);

        if (contentIndex >= 0) {
            setValue(
                `content.${contentIndex}.catalogItemIds`,
                newArticles.map((a) => a.id),
            );
        }
        trigger(["content"]);
    };

    // Add a new tenant tab
    const addTenantTab = (tenant: Tenant) => {
        const currentContent = getValues("content");
        if (currentContent.some((c) => c.tenant === tenant)) {
            return; // Already exists
        }

        setValue("content", [...currentContent, { tenant, catalogItemIds: [] }]);
        trigger(["content"]);
    };

    // Remove a tenant tab (only for non-default tenants)
    const removeTenantTab = (tenant: Tenant) => {
        const currentContent = getValues("content");
        setValue(
            "content",
            currentContent.filter((c) => c.tenant !== tenant),
        );
        trigger(["content"]);
    };

    // Get tenants that can still be added
    const availableTenants = TenantSchema.options.filter(
        (tenant) => !formContent.some((c) => c.tenant === tenant),
    );

    const themeName = (
        <Input.TextArea
            className="textarea__heading"
            placeholder="Name"
            value={defaultValues?.name || ""}
            isValid={() => !errors.name}
            rows={1}
            maxLength={dynamicThemeNameMaxLength}
            autoResize
            trim
            removeLineBreaks
            onChange={(value) => {
                setValue("name", value);
                trigger(["name"]);
            }}
        />
    );

    // Build tabs for TenantTabs
    const tenantTabs = formContent.map((content) => ({
        key: content.tenant,
        children: (
            <TenantTabContent
                tenant={content.tenant}
                initialArticles={getArticlesForTenant(content.tenant)}
                showRemoveTenantButton={content.tenant !== "default"}
                onRemoveTenantClick={() => removeTenantTab(content.tenant as Tenant)}
                onArticlesChange={(newArticles) =>
                    updateArticlesForTenant(content.tenant, newArticles)
                }
            />
        ),
    }));

    const tenantOptions = availableTenants.map((tenant) => ({
        label: formatTenantName(tenant),
        icon: "Add" as const,
        onClick: () => addTenantTab(tenant),
    }));

    const targeting = (
        <TitleValuePair
            title={<Label.Medium.SemiBold.N10>Targeting</Label.Medium.SemiBold.N10>}
            value={
                <Flex.Column gap="xlarge">
                    <Flex.Column gap="large">
                        <h5>Preferences</h5>
                        <SelectTags<ThemePreference>
                            values={ThemePreference.options.map((themePreference) => ({
                                id: themePreference,
                                value:
                                    ThemePreferenceTextMap[themePreference] ||
                                    themePreference,
                                selected: (defaultValues?.preferences || []).includes(
                                    themePreference,
                                ),
                            }))}
                            onChange={(newPreferences) => {
                                setValue(
                                    "preferences",
                                    newPreferences
                                        .filter((it) => it.selected)
                                        .map((it) => it.id),
                                );
                                trigger(["preferences"]);
                            }}
                            selectMultiple
                            size="medium"
                        />
                    </Flex.Column>

                    <Flex.Column gap="large">
                        <h5>Location</h5>

                        <Flex.Row gap="medium">
                            {getValues("location").length === 0 ? (
                                <>
                                    <Tag.Medium.Bold.P90>Global</Tag.Medium.Bold.P90>
                                    <Tag.Medium.Bold.N90>Local</Tag.Medium.Bold.N90>
                                </>
                            ) : (
                                <>
                                    <Tag.Medium.Bold.N90>Global</Tag.Medium.Bold.N90>
                                    <Tag.Medium.Bold.P90>Local</Tag.Medium.Bold.P90>
                                </>
                            )}
                        </Flex.Row>

                        <SelectLocations
                            initialValues={
                                (defaultValues?.location || []) as ThemeLocation[]
                            }
                            onChange={(newLocations) => {
                                setValue("location", newLocations);
                                trigger("location");
                            }}
                        />
                    </Flex.Column>
                </Flex.Column>
            }
        />
    );

    const admin = (
        <TitleValuePair
            title={<Label.Medium.SemiBold.N10>Admin</Label.Medium.SemiBold.N10>}
            value={
                <Flex.Column gap="medium">
                    <Flex.Row gap="medium">
                        <h5>Visibility</h5>
                        <Toggle
                            checked={!!getValues("published")}
                            disabled={!isAdmin}
                            onChange={() => {
                                const value = getValues("published");

                                if (value) {
                                    setValue("published", null);
                                } else {
                                    setValue("published", new Date().toISOString());
                                }
                                trigger(["published"]);
                            }}
                        />

                        {getValues("published") ? (
                            <Tag.Medium.P90>Published</Tag.Medium.P90>
                        ) : (
                            <Tag.Medium.N80>Unpublished</Tag.Medium.N80>
                        )}
                    </Flex.Row>
                    <Label.Medium.Light.N10>
                        {getValues("published")
                            ? "This item can be discovered or promoted in World of Albertine"
                            : "This item can't be discovered or promoted in World of Albertine"}
                    </Label.Medium.Light.N10>
                </Flex.Column>
            }
        />
    );

    // Show loading while fetching missing articles
    if (isFetchingMissingArticles) {
        return (
            <div className="flex w-full flex-1 flex-col items-center justify-center overflow-y-auto">
                <Spinner />
            </div>
        );
    }

    return (
        <Flex.Column className="theme-form">
            <Form.Base
                className="theme-form__form"
                onSubmit={handleSubmit(onSubmit, onInvalid)}
            >
                <Form.Inputs
                    className="theme-form__inputs"
                    ref={scrollElementRef}
                    disabled={loading}
                >
                    {!isValid && isSubmitted && <Form.Errors errors={errorTexts} />}

                    <Form.Spacer size="medium" />
                    <Flex.Column gap="xlarge" alignItems="start">
                        <Flex.Column gap="medium" className="theme-form__name-and-tabs">
                            {themeName}
                            <Form.Spacer size="medium" />
                            <TenantTabs
                                defaultTabText="Default"
                                keepAllTabsRendered
                                tabs={tenantTabs}
                                tenantOptions={tenantOptions}
                            />
                        </Flex.Column>

                        <Form.Divider />

                        {targeting}

                        <Form.Spacer size="small" />
                        <Form.Divider />
                        {admin}
                        <Form.Spacer />
                    </Flex.Column>
                </Form.Inputs>

                <Form.Actions className="theme-form__actions">
                    <Flex.Row justifyContent="space-between">
                        {canDeleteTheme ? (
                            <FlexInline alignSelf="start">
                                <Button.E90
                                    onClick={deleteTheme}
                                    className="theme-form__delete-button"
                                >
                                    Delete
                                </Button.E90>
                            </FlexInline>
                        ) : (
                            <div />
                        )}
                        <FlexInline gap="medium">
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button.N10
                                type="submit"
                                disabled={loading}
                                showLoadingSpinner={loading}
                            >
                                Save
                            </Button.N10>
                        </FlexInline>
                    </Flex.Row>
                </Form.Actions>
            </Form.Base>
        </Flex.Column>
    );
}

type TenantTabContentProps = {
    tenant: ThemeContentTenant;
    initialArticles: CatalogItem[];
    showRemoveTenantButton: boolean;
    onRemoveTenantClick?: () => void;
    onArticlesChange?: (newArticles: CatalogItem[]) => void;
};
function TenantTabContent(props: TenantTabContentProps) {
    const {
        tenant,
        initialArticles,
        showRemoveTenantButton,
        onRemoveTenantClick: onRemoveClick,
        onArticlesChange = () => {},
    } = props;
    return (
        <div className="flex w-full min-w-0 flex-col items-start gap-2">
            <div className="flex w-full items-center justify-between">
                <h4>Add 3-10 articles</h4>
                {showRemoveTenantButton && (
                    <ButtonUILib type="button" variant="ghost" onClick={onRemoveClick}>
                        <XIcon data-icon="inline-start" />
                        <Label className="font-normal text-muted-foreground">
                            Remove tenant
                        </Label>
                    </ButtonUILib>
                )}
            </div>
            <SelectArticles
                initialArticles={initialArticles}
                discoverableForTenant={tenant === "default" ? "all" : tenant}
                onChange={onArticlesChange}
            />
        </div>
    );
}

/**
 * Extract static content from a theme as form content.
 * Returns empty array if theme has dynamic content.
 */
function themeToFormContent(theme: Theme | undefined): StaticThemeFormContent[] {
    if (!theme) {
        return [{ tenant: "default", catalogItemIds: [] }];
    }

    const formContent: StaticThemeFormContent[] = [];
    for (const content of theme.content) {
        if (content.type === "dynamic") {
            // Dynamic content not supported - will be caught at submit time
            continue;
        }
        formContent.push({
            tenant: content.tenant,
            catalogItemIds: content.catalogItemIds,
        });
    }

    // Ensure we always have a default entry
    if (!formContent.some((c) => c.tenant === "default")) {
        formContent.unshift({ tenant: "default", catalogItemIds: [] });
    }

    return formContent;
}

export default ThemeForm;
